/**
 * Water Table 2D Map
 * Shows well locations with water level data on a Leaflet map.
 * Hover tooltips show depth to water and water elevation.
 * Optional contour overlay for water table surface.
 */
import { useMemo, useState } from 'react';
import { MapContainer, CircleMarker, Popup, Polyline, Tooltip as LeafletTooltip } from 'react-leaflet';
import type { WellPoint, WaterTableContour } from '../../api/phreatic';
import { generateContours } from './ContourGenerator';
import { apiClient } from '../../api/client';
import 'leaflet/dist/leaflet.css';
import { EsriVectorBasemap } from '@/lib/basemap';

interface WaterTableMapProps {
  centerLat: number;
  centerLon: number;
  wells: WellPoint[];
  contours?: WaterTableContour | null;
  flowDirection?: number | null;
  selectedWellId?: string | null;
  onWellClick?: (wellId: string) => void;
  siteId?: string;
  onGaugingSaved?: () => void;
}

function getWellColor(well: WellPoint): string {
  if (well.is_dry) return '#ef4444'; // red for dry
  if (well.product_thickness_ft && well.product_thickness_ft > 0) return '#f59e0b'; // amber for product
  return '#06b6d4'; // cyan for normal
}

function getContourColor(index: number, total: number): string {
  // Blue gradient from dark (low elevation) to light (high elevation)
  const t = total > 1 ? index / (total - 1) : 0.5;
  const r = Math.round(20 + t * 60);
  const g = Math.round(80 + t * 100);
  const b = Math.round(180 + t * 75);
  return `rgb(${r},${g},${b})`;
}

export function WaterTableMap({
  centerLat,
  centerLon,
  wells,
  contours,
  flowDirection,
  selectedWellId,
  onWellClick,
  siteId,
  onGaugingSaved,
}: WaterTableMapProps) {
  const [gaugingWell, setGaugingWell] = useState<WellPoint | null>(null);
  const [gaugingDtw, setGaugingDtw] = useState('');
  const [gaugingWe, setGaugingWe] = useState('');
  const [gaugingDate, setGaugingDate] = useState(new Date().toISOString().slice(0, 10));
  const [gaugingSaving, setGaugingSaving] = useState(false);
  const [gaugingMsg, setGaugingMsg] = useState<string | null>(null);

  const handleWellClick = (well: WellPoint) => {
    setGaugingWell(gaugingWell?.well_id === well.well_id ? null : well);
    setGaugingDtw(well.depth_to_water_ft != null ? well.depth_to_water_ft.toFixed(1) : '');
    setGaugingWe(well.water_elevation_ft != null ? well.water_elevation_ft.toFixed(1) : '');
    setGaugingMsg(null);
    onWellClick?.(well.well_id);
  };

  const submitGauging = async () => {
    if (!gaugingWell || !siteId || (!gaugingDtw && !gaugingWe)) return;
    setGaugingSaving(true);
    setGaugingMsg(null);
    try {
      await apiClient.post('/phreatic/pump-control/gauging/manual', {
        site_id: siteId,
        well_name: gaugingWell.well_name,
        depth_to_water_ft: gaugingDtw ? parseFloat(gaugingDtw) : undefined,
        water_elevation_ft: gaugingWe ? parseFloat(gaugingWe) : undefined,
        gauge_date: gaugingDate,
      });
      setGaugingMsg('Saved — V5 predictions will use this reading');
      onGaugingSaved?.();
    } catch (err: any) {
      setGaugingMsg(err?.response?.data?.detail || 'Failed to save');
    } finally {
      setGaugingSaving(false);
    }
  };
  // Compute zoom based on well spread
  const zoom = useMemo(() => {
    if (wells.length < 2) return 15;
    const lats = wells.map(w => w.latitude);
    const lons = wells.map(w => w.longitude);
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lonSpread = Math.max(...lons) - Math.min(...lons);
    const maxSpread = Math.max(latSpread, lonSpread);
    if (maxSpread > 0.05) return 13;
    if (maxSpread > 0.01) return 14;
    if (maxSpread > 0.005) return 15;
    return 16;
  }, [wells]);

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-mono-800">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
          Well Map
        </h3>
        <div className="flex items-center gap-4 text-xs text-mono-500">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" /> Normal
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block" /> Product
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-400 inline-block" /> Dry
          </span>
          {flowDirection != null && (
            <span className="text-blue-400">
              Flow: {flowDirection.toFixed(0)}°
            </span>
          )}
        </div>
      </div>
      <MapContainer
        center={[centerLat, centerLon]}
        zoom={zoom}
        style={{ height: '450px', width: '100%' }}
        className="bg-mono-950"
      >
        <EsriVectorBasemap />

        {/* GIS-style groundwater elevation contours (client-side IDW + marching squares) */}
        {(() => {
          const wellElevs = wells
            .filter(w => w.latitude && w.longitude && w.water_elevation_ft != null && w.water_elevation_ft > 50)
            .map(w => ({ lat: w.latitude, lon: w.longitude, waterElev: w.water_elevation_ft! }));
          const contourLines = generateContours(wellElevs, 50, 12);
          const elevRange = wellElevs.length > 0
            ? Math.max(...wellElevs.map(w => w.waterElev)) - Math.min(...wellElevs.map(w => w.waterElev))
            : 1;
          const minE = wellElevs.length > 0 ? Math.min(...wellElevs.map(w => w.waterElev)) : 0;
          return contourLines.map((cl, idx) => {
            const t = elevRange > 0 ? (cl.elevation - minE) / elevRange : 0.5;
            // Blue gradient: dark blue (low) → light blue (high)
            const r = Math.round(30 + t * 50);
            const g = Math.round(80 + t * 120);
            const b = Math.round(180 + t * 75);
            return (
              <Polyline key={`gw-contour-${idx}`}
                positions={cl.points}
                pathOptions={{ color: `rgb(${r},${g},${b})`, weight: 2, opacity: 0.8 }}>
                <LeafletTooltip permanent={false} direction="center" className="!bg-mono-900 !border-mono-700 !text-mono-200 !text-[10px] !px-1.5 !py-0.5 !rounded">
                  {cl.elevation.toFixed(0)} ft MSL
                </LeafletTooltip>
              </Polyline>
            );
          });
        })()}

        {/* Well markers */}
        {wells.map((well) => (
          <CircleMarker
            key={well.well_id}
            center={[well.latitude, well.longitude]}
            radius={selectedWellId === well.well_id ? 10 : 7}
            pathOptions={{
              color: selectedWellId === well.well_id ? '#fff' : getWellColor(well),
              fillColor: getWellColor(well),
              fillOpacity: 0.8,
              weight: selectedWellId === well.well_id ? 3 : 2,
            }}
            eventHandlers={{
              click: () => handleWellClick(well),
            }}
          >
            <LeafletTooltip direction="top" offset={[0, -8]} className="well-tooltip">
              <div className="text-xs">
                <div className="font-bold">{well.well_name}</div>
                {well.depth_to_water_ft != null && (
                  <div>DTW: {well.depth_to_water_ft.toFixed(2)} ft</div>
                )}
                {well.water_elevation_ft != null && (
                  <div>WE: {well.water_elevation_ft.toFixed(2)} ft</div>
                )}
                {well.product_thickness_ft != null && well.product_thickness_ft > 0 && (
                  <div className="text-cyan-500">Product: {well.product_thickness_ft.toFixed(2)} ft</div>
                )}
                {well.is_dry && <div className="text-red-500">DRY</div>}
                {well.gauge_date && (
                  <div className="text-gray-400">{new Date(well.gauge_date + 'T00:00:00').toLocaleDateString()}</div>
                )}
              </div>
            </LeafletTooltip>
            <Popup>
              <div className="text-xs space-y-1">
                <div className="font-bold text-sm">{well.well_name}</div>
                {well.ground_elevation_ft != null && <div>Ground Elev: {well.ground_elevation_ft.toFixed(1)} ft</div>}
                {well.total_depth_ft != null && <div>Total Depth: {well.total_depth_ft.toFixed(1)} ft</div>}
                {well.depth_to_water_ft != null && <div>Depth to Water: {well.depth_to_water_ft.toFixed(2)} ft</div>}
                {well.water_elevation_ft != null && <div>Water Elevation: {well.water_elevation_ft.toFixed(2)} ft MSL</div>}
                {well.product_thickness_ft != null && well.product_thickness_ft > 0 && (
                  <div className="text-cyan-600">Product Thickness: {well.product_thickness_ft.toFixed(2)} ft</div>
                )}
                {well.gauge_date && <div className="text-gray-500">Date: {well.gauge_date}</div>}
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>

      {/* Manual Gauging Input — appears when a well is clicked */}
      {gaugingWell && siteId && (
        <div className="p-3 border-t border-mono-800 bg-mono-800/30">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-xs text-cyan-300 font-semibold">{gaugingWell.well_name}</div>
            <div>
              <label className="text-[9px] text-mono-500">DTW (ft)</label>
              <input type="number" step="0.1" value={gaugingDtw} onChange={e => setGaugingDtw(e.target.value)}
                placeholder="85.3" className="w-24 ml-1 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
            </div>
            <div>
              <label className="text-[9px] text-mono-500">or WE (ft MSL)</label>
              <input type="number" step="0.1" value={gaugingWe} onChange={e => setGaugingWe(e.target.value)}
                placeholder="4350" className="w-24 ml-1 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
            </div>
            <div>
              <label className="text-[9px] text-mono-500">Date</label>
              <input type="date" value={gaugingDate} onChange={e => setGaugingDate(e.target.value)}
                className="ml-1 px-2 py-1 text-xs bg-mono-800 border border-mono-700 rounded text-mono-200" />
            </div>
            <button onClick={submitGauging} disabled={gaugingSaving || (!gaugingDtw && !gaugingWe)}
              className="px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
              {gaugingSaving ? 'Saving...' : 'Submit Reading'}
            </button>
            <button onClick={() => setGaugingWell(null)}
              className="px-2 py-1 text-xs text-mono-500 hover:text-mono-300">Cancel</button>
            {gaugingMsg && (
              <span className={`text-[10px] ${gaugingMsg.includes('Saved') ? 'text-cyan-400' : 'text-red-400'}`}>
                {gaugingMsg}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
